export interface CompressionOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
  maxSizeMB?: number;
}

export interface CompressionResult {
  blob: Blob;
  width: number;
  height: number;
  originalSize: number;
  compressedSize: number;
  compressionRatio: number;
}

const defaultOptions: CompressionOptions = {
  maxWidth: 1920,
  maxHeight: 1920,
  quality: 0.8,
  maxSizeMB: 0.5
};

export async function compressImage(
  file: File,
  options: CompressionOptions = {}
): Promise<CompressionResult> {
  const opts = { ...defaultOptions, ...options };
  const originalSize = file.size;

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target?.result as string;
      
      img.onload = () => {
        let { width, height } = img;
        
        if (width > opts.maxWidth! || height > opts.maxHeight!) {
          const aspectRatio = width / height;
          
          if (width > height) {
            width = Math.min(width, opts.maxWidth!);
            height = width / aspectRatio;
          } else {
            height = Math.min(height, opts.maxHeight!);
            width = height * aspectRatio;
          }
        }
        
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }
        
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);
        
        let quality = opts.quality!;
        
        const tryCompress = (q: number): void => {
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error('Failed to compress image'));
                return;
              }
              
              const maxBytes = opts.maxSizeMB! * 1024 * 1024;
              
              if (blob.size > maxBytes && q > 0.3) {
                tryCompress(q - 0.1);
              } else {
                resolve({
                  blob,
                  width: Math.round(width),
                  height: Math.round(height),
                  originalSize,
                  compressedSize: blob.size,
                  compressionRatio: originalSize / blob.size
                });
              }
            },
            'image/jpeg',
            q
          );
        };
        
        tryCompress(quality);
      };
      
      img.onerror = () => {
        reject(new Error('Failed to load image'));
      };
    };
    
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
  });
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export async function compressImageToFile(
  file: File,
  options: CompressionOptions = {}
): Promise<File> {
  const result = await compressImage(file, options);
  const extension = 'jpg';
  const baseName = file.name.replace(/\.[^/.]+$/, '');
  
  const compressedFile = new File(
    [result.blob],
    `${baseName}.${extension}`,
    { type: 'image/jpeg', lastModified: Date.now() }
  );
  
  console.log(
    `[Image Compression] ${file.name}: ${formatBytes(result.originalSize)} -> ${formatBytes(result.compressedSize)} (${((1 - result.compressedSize / result.originalSize) * 100).toFixed(0)}% reduction)`
  );
  
  return compressedFile;
}
